// Shared fields for the Add / Edit Flash Sale dialog and the Deal of the Day
// form:
//   <OfferOnToggle>        "Offer on: A product | A category"
//   <CategoryOfferFields>  category picker + optional title (+ up to % off)
//   <OfferImageField>      optional own picture for the entry, with the
//                          recommended size; uploaded at once, the filename
//                          is sent with the entry as offer_image.
// In the shop the own picture is shown instead of the product / category
// image; without it the product / category main image is used.
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CategoryOutlined, Delete, Inventory2Outlined, UploadFile } from '@mui/icons-material';
import { adminPost, imageUrl } from './CategoryExtraProducts.tsx';
import { IMAGE_SPECS, checkImage } from '../../utils/imageCheck.tsx';

const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';
const base = () => String(process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');

export const OFFER_IMAGE_GUIDE = 'Square image, 800 × 800 px recommended (at least 600 × 600). JPG or WebP at 80–85% quality, under 250 KB. Keep the product in the middle with some space around it - the app shows it as a square.';

export type OfferOn = 'product' | 'category';

export function OfferOnToggle({ value, onChange, disabled }: { value: OfferOn; onChange: (v: OfferOn) => void; disabled?: boolean }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#202223' }}>
        Offer on
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        disabled={disabled}
        onChange={(_, v) => v && onChange(v)}
        sx={{
          '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2.5, color: '#6d7175', borderColor: '#c9cccf', gap: 0.75 },
          '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white !important' },
        }}
      >
        <ToggleButton value="product"><Inventory2Outlined sx={{ fontSize: 17 }} />A product</ToggleButton>
        <ToggleButton value="category"><CategoryOutlined sx={{ fontSize: 17 }} />A category</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {value === 'product'
          ? 'Customers tap it to open the product page.'
          : "Customers tap it to open the category's product list (its own + extra products from Category Management)."}
      </Typography>
    </Box>
  );
}

export type CategoryOfferValue = { category_id: string; offer_title: string; discount: string };

// categories for the picker (Back Office sees hidden festival categories too)
export function useOfferCategories() {
  const [list, setList] = useState<any[]>([]);
  useEffect(() => {
    let alive = true;
    adminPost('category/list', { start: 0, limit: 500 })
      .then(d => alive && setList(Array.isArray(d?.result) ? d.result : []))
      .catch(() => alive && setList([]));
    return () => {
      alive = false;
    };
  }, []);
  return list;
}

export function CategoryOfferFields({
  value,
  onChange,
  disabled,
  showDiscount = true,
  error,
}: {
  value: CategoryOfferValue;
  onChange: (v: CategoryOfferValue) => void;
  disabled?: boolean;
  showDiscount?: boolean;
  error?: string;
}) {
  const categories = useOfferCategories();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>Category *</Typography>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={value.category_id}
          onChange={e => onChange({ ...value, category_id: String(e.target.value) })}
          disabled={disabled}
          error={!!error}
        >
          <MenuItem value="" disabled>Choose a category</MenuItem>
          {categories.map((c: any) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.cat_name}
              {Number(c.show_in_list ?? 1) === 0 ? '  · hidden from lists' : ''}
              {Number(c.status) === 0 ? '  · inactive' : ''}
            </MenuItem>
          ))}
        </Select>
        {!!error && <Typography variant="caption" color="error">{error}</Typography>}
      </Box>
      <TextField
        size="small"
        label="Title shown to customers (optional)"
        placeholder="Uses the category name when empty"
        value={value.offer_title}
        onChange={e => onChange({ ...value, offer_title: e.target.value })}
        disabled={disabled}
      />
      {showDiscount && (
        <TextField
          size="small"
          label="Up to % off (optional)"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          helperText="Empty: the best discount among the category's products is shown"
          value={value.discount}
          onChange={e => onChange({ ...value, discount: e.target.value })}
          disabled={disabled}
        />
      )}
    </Box>
  );
}

// Upload right away; returns the stored filename.
async function uploadOfferImage(file: File): Promise<string> {
  const token = localStorage.getItem('admin_token') || '';
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${base()}/user/offerimage`, {
    method: 'POST',
    headers: { authkey: AUTH_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: fd,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok || data?.error) throw new Error(data?.message || `Upload failed (${res.status})`);
  return String(data.filename || '');
}


export function OfferImageField({
  value,
  onChange,
  disabled,
  fallbackUrl,
  onError,
}: {
  onError?: (message: string) => void; // errors as a popup (page snackbar)
  value: string; // stored filename ('' = none)
  onChange: (filename: string) => void;
  disabled?: boolean;
  fallbackUrl?: string; // product / category image shown when there is no own picture
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [hint, setHint] = useState('');

  const fail = (m: string) => (onError ? onError(m) : setProblem(m));
  const pick = async (file?: File | null) => {
    if (!file) return;
    setProblem('');
    setHint('');
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      fail('Only JPG, PNG or WebP images are allowed.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      fail('Image too large - max 2 MB.');
      return;
    }
    // size / shape / quality check (popup when something is off)
    const picked = await checkImage(file, IMAGE_SPECS.offerImage); // original or resized
    if (!picked) return;
    file = picked;
    setBusy(true);
    try {
      onChange(await uploadOfferImage(file));
    } catch (e: any) {
      fail(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const shown = value ? imageUrl(value) : fallbackUrl;
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#202223' }}>
        Image (optional)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            flexShrink: 0,
            borderRadius: '12px',
            border: '1px solid #E6E1DD',
            bgcolor: '#F5F2EF',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {busy ? (
            <CircularProgress size={22} />
          ) : shown ? (
            <img src={shown} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <UploadFile sx={{ color: '#b9b0a9' }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {value ? 'Own image - shown instead of the product / category picture.' : 'No own image - the product / category main picture is shown.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadFile />}
              disabled={disabled || busy}
              onClick={() => input.current?.click()}
              sx={{ bgcolor: '#202223', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#303030' } }}
            >
              {value ? 'Change image' : 'Upload image'}
            </Button>
            {!!value && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Delete />}
                disabled={disabled || busy}
                onClick={() => onChange('')}
                sx={{ bgcolor: '#f1f2f4', color: '#202223', textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#e4e5e7', boxShadow: 'none' } }}
              >
                Remove
              </Button>
            )}
          </Box>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={e => pick(e.target.files?.[0])}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
            {OFFER_IMAGE_GUIDE}
          </Typography>
        </Box>
      </Box>
      {!!problem && <Alert severity="error" sx={{ mt: 1 }}>{problem}</Alert>}
      {!!hint && <Alert severity="warning" sx={{ mt: 1 }}>{hint}</Alert>}
    </Box>
  );
}
